import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Package, Search, Loader2, X, Plus,
  AlertTriangle, CheckCircle2, Edit, Trash2, History,
  ArrowDownCircle, ArrowUpCircle, Link2, Unlink, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;

interface Ingredient {
  id: string;
  restaurant_id: string;
  name: string;
  quantity: number;
  min_quantity: number;
  unit: string;
  cost_per_unit: number;
  is_active: boolean;
  created_at: string;
  linked_products: { product_id: string; product_name: string; quantity_used: number }[];
}

interface Movement {
  id: string;
  ingredient_id: string;
  quantity_change: number;
  reason: string;
  notes: string | null;
  created_at: string;
}

const UNITS = [
  { value: "un", label: "Unidade" },
  { value: "kg", label: "Quilograma" },
  { value: "g", label: "Grama" },
  { value: "L", label: "Litro" },
  { value: "ml", label: "Mililitro" },
  { value: "cx", label: "Caixa" },
  { value: "pct", label: "Pacote" },
  { value: "dz", label: "Dúzia" },
];

const REASONS: Record<string, string> = {
  manual: "Ajuste manual",
  purchase: "Compra / Entrada",
  sale: "Consumo / Saída",
  waste: "Desperdício",
  return: "Devolução",
  correction: "Correção de inventário",
};

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;
const formatDateTime = (d: string) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }) +
  " " + new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

const Estoque = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Ingredient form
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fName, setFName] = useState("");
  const [fQty, setFQty] = useState(0);
  const [fMinQty, setFMinQty] = useState(0);
  const [fUnit, setFUnit] = useState("un");
  const [fCost, setFCost] = useState(0);
  const [saving, setSaving] = useState(false);

  // Movement
  const [movementOpen, setMovementOpen] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [mvtType, setMvtType] = useState<"in" | "out">("in");
  const [mvtQty, setMvtQty] = useState("");
  const [mvtReason, setMvtReason] = useState("manual");
  const [mvtNotes, setMvtNotes] = useState("");

  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [movements, setMovements] = useState<Movement[]>([]);

  // Link products
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkSelections, setLinkSelections] = useState<Record<string, { checked: boolean; qty: number }>>({});

  const fetchData = useCallback(async (restId: string) => {
    const [{ data: ings }, { data: prods }, { data: pIngredients }] = await Promise.all([
      supabase.from("ingredients").select("*").eq("restaurant_id", restId).order("name"),
      supabase.from("products").select("*").eq("restaurant_id", restId),
      supabase.from("product_ingredients").select("*"),
    ]);

    const allProducts = prods || [];
    setProducts(allProducts);

    const allPIngredients = (pIngredients || []) as any[];

    const mapped: Ingredient[] = (ings || []).map((ing: any) => {
      const links = allPIngredients
        .filter((pi: any) => pi.ingredient_id === ing.id)
        .map((pi: any) => {
          const prod = allProducts.find((p) => p.id === pi.product_id);
          return {
            product_id: pi.product_id,
            product_name: prod?.name || "Produto removido",
            quantity_used: Number(pi.quantity_used),
          };
        });

      return {
        id: ing.id,
        restaurant_id: ing.restaurant_id,
        name: ing.name,
        quantity: Number(ing.quantity),
        min_quantity: Number(ing.min_quantity),
        unit: ing.unit,
        cost_per_unit: Number(ing.cost_per_unit),
        is_active: ing.is_active,
        created_at: ing.created_at,
        linked_products: links,
      };
    });

    setIngredients(mapped);
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        const { data } = await supabase
          .from("restaurants").select("id").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase
          .from("restaurants").select("id").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurantId(rest.id);
      await fetchData(rest.id);
      setLoading(false);
    };
    init();
  }, [navigate, fetchData]);

  // Stats
  const stats = useMemo(() => {
    const active = ingredients.filter((i) => i.is_active);
    return {
      total: ingredients.length,
      low: active.filter((i) => i.quantity > 0 && i.quantity <= i.min_quantity).length,
      out: active.filter((i) => i.quantity <= 0).length,
      totalCost: active.reduce((s, i) => s + i.quantity * i.cost_per_unit, 0),
    };
  }, [ingredients]);

  // Filter
  const filtered = useMemo(() => {
    let result = [...ingredients];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((i) =>
        i.name.toLowerCase().includes(q) ||
        i.linked_products.some((lp) => lp.product_name.toLowerCase().includes(q))
      );
    }
    if (statusFilter === "low") result = result.filter((i) => i.quantity > 0 && i.quantity <= i.min_quantity);
    else if (statusFilter === "out") result = result.filter((i) => i.quantity <= 0);
    else if (statusFilter === "ok") result = result.filter((i) => i.quantity > i.min_quantity);
    return result;
  }, [ingredients, search, statusFilter]);

  const getStatus = (ing: Ingredient) => {
    if (ing.quantity <= 0) return { label: "Esgotado", color: "bg-destructive/10 text-destructive border-destructive/30" };
    if (ing.quantity <= ing.min_quantity) return { label: "Baixo", color: "bg-warning/10 text-warning border-warning/30" };
    return { label: "OK", color: "bg-success/10 text-success border-success/30" };
  };

  // CRUD
  const openNew = () => {
    setEditingId(null);
    setFName("");
    setFQty(0);
    setFMinQty(0);
    setFUnit("un");
    setFCost(0);
    setFormOpen(true);
  };

  const openEdit = (ing: Ingredient) => {
    setEditingId(ing.id);
    setFName(ing.name);
    setFQty(ing.quantity);
    setFMinQty(ing.min_quantity);
    setFUnit(ing.unit);
    setFCost(ing.cost_per_unit);
    setFormOpen(true);
  };

  const saveIngredient = async () => {
    if (!restaurantId || !fName.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase.from("ingredients").update({
          name: fName.trim(), quantity: fQty, min_quantity: fMinQty, unit: fUnit, cost_per_unit: fCost,
        }).eq("id", editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("ingredients").insert({
          restaurant_id: restaurantId,
          name: fName.trim(), quantity: fQty, min_quantity: fMinQty, unit: fUnit, cost_per_unit: fCost,
        });
        if (error) throw error;
      }
      toast({ title: editingId ? "Ingrediente atualizado!" : "Ingrediente adicionado!" });
      setFormOpen(false);
      await fetchData(restaurantId);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const deleteIngredient = async (id: string) => {
    if (!restaurantId) return;
    const { error } = await supabase.from("ingredients").delete().eq("id", id);
    if (error) {
      toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Ingrediente excluído!" });
      await fetchData(restaurantId);
    }
  };

  // Movements
  const openMovement = (ing: Ingredient) => {
    setSelectedIngredient(ing);
    setMvtType("in");
    setMvtQty("");
    setMvtReason("manual");
    setMvtNotes("");
    setMovementOpen(true);
  };

  const saveMovement = async () => {
    if (!selectedIngredient || !restaurantId) return;
    const qty = parseFloat(mvtQty);
    if (isNaN(qty) || qty <= 0) {
      toast({ title: "Informe uma quantidade válida", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const change = mvtType === "in" ? qty : -qty;
      const newQty = Math.max(0, selectedIngredient.quantity + change);

      const { error: upErr } = await supabase.from("ingredients")
        .update({ quantity: newQty }).eq("id", selectedIngredient.id);
      if (upErr) throw upErr;

      const { error: mvErr } = await supabase.from("ingredient_movements").insert({
        ingredient_id: selectedIngredient.id,
        restaurant_id: restaurantId,
        quantity_change: change,
        reason: mvtReason,
        notes: mvtNotes || null,
      });
      if (mvErr) throw mvErr;

      toast({ title: `${mvtType === "in" ? "Entrada" : "Saída"} registrada!` });
      setMovementOpen(false);
      await fetchData(restaurantId);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // History
  const openHistory = async (ing: Ingredient) => {
    setSelectedIngredient(ing);
    const { data } = await supabase.from("ingredient_movements")
      .select("*").eq("ingredient_id", ing.id).order("created_at", { ascending: false }).limit(50);
    setMovements((data as Movement[]) || []);
    setHistoryOpen(true);
  };

  // Link products
  const openLink = (ing: Ingredient) => {
    setSelectedIngredient(ing);
    const selections: Record<string, { checked: boolean; qty: number }> = {};
    products.forEach((p) => {
      const link = ing.linked_products.find((lp) => lp.product_id === p.id);
      selections[p.id] = { checked: !!link, qty: link?.quantity_used || 1 };
    });
    setLinkSelections(selections);
    setLinkOpen(true);
  };

  const saveLinks = async () => {
    if (!selectedIngredient || !restaurantId) return;
    setSaving(true);
    try {
      // Delete existing links
      await supabase.from("product_ingredients")
        .delete().eq("ingredient_id", selectedIngredient.id);

      // Insert selected ones
      const inserts = Object.entries(linkSelections)
        .filter(([, v]) => v.checked)
        .map(([productId, v]) => ({
          product_id: productId,
          ingredient_id: selectedIngredient.id,
          quantity_used: v.qty,
        }));

      if (inserts.length > 0) {
        const { error } = await supabase.from("product_ingredients").insert(inserts);
        if (error) throw error;
      }

      toast({ title: "Vínculos atualizados!" });
      setLinkOpen(false);
      await fetchData(restaurantId);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Package className="h-5 w-5 text-primary" />
            <h1 className="font-display font-bold text-lg">Estoque de Ingredientes</h1>
            <Badge variant="secondary" className="text-xs">{filtered.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/relatorio-custos")} className="gap-2 hidden sm:flex">
              <BarChart3 className="h-4 w-4" /> Custos
            </Button>
            <Button onClick={openNew} className="bg-gradient-primary text-primary-foreground gap-2">
              <Plus className="h-4 w-4" /> Novo Ingrediente
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-5 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: String(stats.total), icon: Package, accent: "text-foreground" },
            { label: "Estoque Baixo", value: String(stats.low), icon: AlertTriangle, accent: "text-warning" },
            { label: "Esgotados", value: String(stats.out), icon: AlertTriangle, accent: "text-destructive" },
            { label: "Custo em Estoque", value: formatCurrency(stats.totalCost), icon: ArrowDownCircle, accent: "text-primary" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-gradient-card shadow-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.icon className={`h-4 w-4 ${s.accent}`} />
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <p className={`text-xl font-bold font-display ${s.accent}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar ingrediente ou produto..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-card border-border" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-10 bg-card border-border">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="ok">✅ Em estoque</SelectItem>
              <SelectItem value="low">⚠️ Estoque baixo</SelectItem>
              <SelectItem value="out">🚫 Esgotado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="rounded-xl border border-border bg-gradient-card shadow-card p-12 text-center">
            <Package className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">
              {ingredients.length === 0 ? "Nenhum ingrediente cadastrado" : "Nenhum resultado encontrado"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {ingredients.length === 0 ? "Clique em \"Novo Ingrediente\" para começar" : "Tente ajustar os filtros"}
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-gradient-card shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b border-border bg-secondary/30">
                    <th className="text-left p-3 font-medium">Ingrediente</th>
                    <th className="text-center p-3 font-medium">Quantidade</th>
                    <th className="text-center p-3 font-medium hidden sm:table-cell">Mínimo</th>
                    <th className="text-center p-3 font-medium hidden md:table-cell">Custo Unit.</th>
                    <th className="text-center p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium hidden lg:table-cell">Produtos Vinculados</th>
                    <th className="text-right p-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {filtered.map((ing) => {
                      const st = getStatus(ing);
                      return (
                        <motion.tr
                          key={ing.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          className="border-b border-border/50 hover:bg-secondary/20 transition-colors"
                        >
                          <td className="p-3">
                            <p className="text-sm font-medium">{ing.name}</p>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-sm font-bold">
                              {ing.quantity} <span className="text-xs text-muted-foreground font-normal">{ing.unit}</span>
                            </span>
                          </td>
                          <td className="p-3 text-center hidden sm:table-cell">
                            <span className="text-sm text-muted-foreground">{ing.min_quantity} {ing.unit}</span>
                          </td>
                          <td className="p-3 text-center hidden md:table-cell">
                            <span className="text-sm text-muted-foreground">{formatCurrency(ing.cost_per_unit)}</span>
                          </td>
                          <td className="p-3 text-center">
                            <Badge variant="outline" className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                          </td>
                          <td className="p-3 hidden lg:table-cell">
                            {ing.linked_products.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {ing.linked_products.slice(0, 3).map((lp) => (
                                  <Badge key={lp.product_id} variant="secondary" className="text-[10px]">
                                    {lp.product_name} ({lp.quantity_used}{ing.unit})
                                  </Badge>
                                ))}
                                {ing.linked_products.length > 3 && (
                                  <Badge variant="secondary" className="text-[10px]">+{ing.linked_products.length - 3}</Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">Nenhum</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" title="Entrada/Saída" onClick={() => openMovement(ing)}>
                                <ArrowDownCircle className="h-4 w-4 text-success" />
                              </Button>
                              <Button variant="ghost" size="sm" title="Vincular produtos" onClick={() => openLink(ing)}>
                                <Link2 className="h-4 w-4 text-primary" />
                              </Button>
                              <Button variant="ghost" size="sm" title="Histórico" onClick={() => openHistory(ing)}>
                                <History className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" title="Editar" onClick={() => openEdit(ing)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" title="Excluir" onClick={() => deleteIngredient(ing.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Ingredient Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">{editingId ? "Editar" : "Novo"} Ingrediente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Ex: Queijo mussarela, Tomate..." className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Quantidade atual</Label>
                <Input type="number" min={0} step="0.01" value={fQty} onChange={(e) => setFQty(Number(e.target.value))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Quantidade mínima</Label>
                <Input type="number" min={0} step="0.01" value={fMinQty} onChange={(e) => setFMinQty(Number(e.target.value))} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Unidade</Label>
                <Select value={fUnit} onValueChange={setFUnit}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (<SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Custo unitário (R$)</Label>
                <Input type="number" min={0} step="0.01" value={fCost} onChange={(e) => setFCost(Number(e.target.value))} className="mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
            <Button onClick={saveIngredient} disabled={saving || !fName.trim()} className="bg-gradient-primary text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={movementOpen} onOpenChange={setMovementOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Movimentação</DialogTitle>
          </DialogHeader>
          {selectedIngredient && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <strong>{selectedIngredient.name}</strong> — Estoque: <strong>{selectedIngredient.quantity} {selectedIngredient.unit}</strong>
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={mvtType === "in" ? "default" : "outline"}
                  className={mvtType === "in" ? "bg-success text-white hover:bg-success/90" : ""}
                  onClick={() => setMvtType("in")}
                >
                  <ArrowDownCircle className="h-4 w-4 mr-2" /> Entrada
                </Button>
                <Button
                  variant={mvtType === "out" ? "default" : "outline"}
                  className={mvtType === "out" ? "bg-destructive text-white hover:bg-destructive/90" : ""}
                  onClick={() => setMvtType("out")}
                >
                  <ArrowUpCircle className="h-4 w-4 mr-2" /> Saída
                </Button>
              </div>
              <div>
                <Label className="text-xs">Quantidade ({selectedIngredient.unit})</Label>
                <Input type="number" min={0} step="0.01" placeholder="0" value={mvtQty} onChange={(e) => setMvtQty(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Motivo</Label>
                <Select value={mvtReason} onValueChange={setMvtReason}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(REASONS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Observações (opcional)</Label>
                <Textarea value={mvtNotes} onChange={(e) => setMvtNotes(e.target.value)} placeholder="Ex: Compra no fornecedor X..." className="mt-1" rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovementOpen(false)}>Cancelar</Button>
            <Button onClick={saveMovement} disabled={saving} className="bg-gradient-primary text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Products Dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              Vincular a Produtos
            </DialogTitle>
          </DialogHeader>
          {selectedIngredient && (
            <>
              <p className="text-sm text-muted-foreground">
                Selecione os produtos que utilizam <strong>{selectedIngredient.name}</strong> e informe a quantidade consumida por unidade produzida.
              </p>
              <div className="space-y-2 mt-2">
                {products.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Nenhum produto cadastrado</p>
                ) : (
                  products.map((p) => {
                    const sel = linkSelections[p.id] || { checked: false, qty: 1 };
                    return (
                      <div key={p.id} className="flex items-center gap-3 rounded-lg border border-border p-3">
                        <Checkbox
                          checked={sel.checked}
                          onCheckedChange={(checked) =>
                            setLinkSelections((prev) => ({
                              ...prev,
                              [p.id]: { ...prev[p.id], checked: !!checked },
                            }))
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.name}</p>
                          <p className="text-[11px] text-muted-foreground">{formatCurrency(Number(p.price))}</p>
                        </div>
                        {sel.checked && (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min={0.01}
                              step="0.01"
                              value={sel.qty}
                              onChange={(e) =>
                                setLinkSelections((prev) => ({
                                  ...prev,
                                  [p.id]: { ...prev[p.id], qty: Number(e.target.value) },
                                }))
                              }
                              className="w-20 h-8 text-xs"
                            />
                            <span className="text-xs text-muted-foreground">{selectedIngredient.unit}</span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Cancelar</Button>
            <Button onClick={saveLinks} disabled={saving} className="bg-gradient-primary text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar Vínculos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Histórico — {selectedIngredient?.name}
            </DialogTitle>
          </DialogHeader>
          {movements.length === 0 ? (
            <div className="text-center py-8">
              <History className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Nenhuma movimentação registrada</p>
            </div>
          ) : (
            <div className="space-y-2">
              {movements.map((m) => (
                <div key={m.id} className="rounded-lg border border-border p-3 flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    m.quantity_change > 0 ? "bg-success/10" : "bg-destructive/10"
                  }`}>
                    {m.quantity_change > 0
                      ? <ArrowDownCircle className="h-4 w-4 text-success" />
                      : <ArrowUpCircle className="h-4 w-4 text-destructive" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-bold ${m.quantity_change > 0 ? "text-success" : "text-destructive"}`}>
                        {m.quantity_change > 0 ? "+" : ""}{m.quantity_change}
                      </span>
                      <span className="text-[11px] text-muted-foreground">{formatDateTime(m.created_at)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{REASONS[m.reason] || m.reason}</p>
                    {m.notes && <p className="text-xs text-muted-foreground mt-1 italic">{m.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Estoque;
